import { Injectable } from '@angular/core';
import { CalendarEventColorTheme } from '@sb/library/base';
import { EventColorEnum } from '@sb/library/data-models';

export class ColorOption {
    ColorName!: string;
    Color!: EventColorEnum;
}

export class ColorGroup {
    Name!: string;
    Colors!: Array<ColorOption>;
}

export type BrandScale = Record<number, string>;

type HslColor = {
    h: number;
    s: number;
    l: number;
};

const BRAND_STOPS: number[] = [25, 50, 100, 150, 200, 300, 400, 500, 600, 700, 800, 850];

@Injectable({
    providedIn: 'root'
})
export class StyleManager {
    private _defaultBrandScaleFromTokens: BrandScale | null = null;
    private _defaultBrandColorFromTokens: string | null = null;
    private _brandDependentTokens: Map<string, string> | null = null;

    private _eventColorClassMapping = {
        [EventColorEnum.Beach]: { Color: 'beach', Name: $localize`:@@CommonColorBeach:Beach` },
        [EventColorEnum.Black]: { Color: 'black', Name: $localize`:@@CommonColorBlack:Black` },
        [EventColorEnum.Blue]: { Color: 'blue', Name: $localize`:@@CommonColorBlue:Blue` },
        [EventColorEnum.Brown]: { Color: 'brown', Name: $localize`:@@CommonColorBrown:Brown` },
        [EventColorEnum.DarkOrange]: { Color: 'darkorange', Name: $localize`:@@CommonColorDarkOrange:Dark Orange` },
        [EventColorEnum.Gray]: { Color: 'gray', Name: $localize`:@@CommonColorGray:Gray` },
        [EventColorEnum.Green]: { Color: 'green', Name: $localize`:@@CommonColorGreen:Green` },
        [EventColorEnum.GrassGreen]: { Color: 'grassgreen', Name: $localize`:@@CommonColorGrassGreen:Grass Green` },
        [EventColorEnum.Lavender]: { Color: 'lavender', Name: $localize`:@@CommonColorLavender:Lavender` },
        [EventColorEnum.LightGrey]: { Color: 'lightgrey', Name: $localize`:@@CommonColorLightGrey:Light Grey` },
        [EventColorEnum.LightPink]: { Color: 'lightpink', Name: $localize`:@@CommonColorLightPink:Light Pink` },
        [EventColorEnum.Lime]: { Color: 'lime', Name: $localize`:@@CommonColorLime:Lime` },
        [EventColorEnum.Magenta]: { Color: 'magenta', Name: $localize`:@@CommonColorMagenta:Magenta` },
        [EventColorEnum.Mint]: { Color: 'mint', Name: $localize`:@@CommonColorMint:Mint` },
        [EventColorEnum.Olive]: { Color: 'olive', Name: $localize`:@@CommonColorOlive:Olive` },
        [EventColorEnum.Orange]: { Color: 'orange', Name: $localize`:@@CommonColorOrange:Orange` },
        [EventColorEnum.Pink]: { Color: 'pink', Name: $localize`:@@CommonColorPink:Pink` },
        [EventColorEnum.Purple]: { Color: 'purple', Name: $localize`:@@CommonColorPurple:Purple` },
        [EventColorEnum.Red]: { Color: 'red', Name: $localize`:@@CommonColorRed:Red` },
        [EventColorEnum.Royal]: { Color: 'royal', Name: $localize`:@@CommonColorRoyal:Royal` },
        [EventColorEnum.Ruby]: { Color: 'ruby', Name: $localize`:@@CommonColorRuby:Ruby` },
        [EventColorEnum.Salmon]: { Color: 'salmon', Name: $localize`:@@CommonColorSalmon:Salmon` },
        [EventColorEnum.SeaBlue]: { Color: 'seablue', Name: $localize`:@@CommonColorSeaBlue:Sea Blue` },
        [EventColorEnum.Tan]: { Color: 'tan', Name: $localize`:@@CommonColorTan:Tan` },
        [EventColorEnum.Tangerine]: { Color: 'tangerine', Name: $localize`:@@CommonColorTangerine:Tangerine` },
        [EventColorEnum.Teal]: { Color: 'teal', Name: $localize`:@@CommonColorTeal:Teal` },
        [EventColorEnum.Yellow]: { Color: 'yellow', Name: $localize`:@@CommonColorYellow:Yellow` },
        [EventColorEnum.White]: { Color: 'white', Name: $localize`:@@CommonColorWhite:White` }
    };

    readonly StandardColors: EventColorEnum[] = [
        EventColorEnum.Red,
        EventColorEnum.Orange,
        EventColorEnum.Yellow,
        EventColorEnum.Green,
        EventColorEnum.Blue,
        EventColorEnum.Purple,
        EventColorEnum.Pink
    ];

    readonly AdditionalColors: EventColorEnum[] = [
        EventColorEnum.Beach,
        EventColorEnum.Black,
        EventColorEnum.Brown,
        EventColorEnum.DarkOrange,
        EventColorEnum.Gray,
        EventColorEnum.GrassGreen,
        EventColorEnum.Lavender,
        EventColorEnum.LightGrey,
        EventColorEnum.LightPink,
        EventColorEnum.Lime,
        EventColorEnum.Magenta,
        EventColorEnum.Mint,
        EventColorEnum.Olive,
        EventColorEnum.Royal,
        EventColorEnum.Ruby,
        EventColorEnum.Salmon,
        EventColorEnum.SeaBlue,
        EventColorEnum.Tan,
        EventColorEnum.Tangerine,
        EventColorEnum.Teal,
        EventColorEnum.White
    ];

    BuildColorGroups(): ColorGroup[] {
        const createColorMap = (colors: EventColorEnum[]): ColorOption[] =>
            colors.map(color => ({
                Color: color,
                ColorName: this.GetColorDisplayName(color)
            }));

        return [
            { Name: $localize`:@@TPPostsColorGroupBasics:Standard Colors`, Colors: createColorMap(this.StandardColors) },
            { Name: $localize`:@@TPPostsColorGroupAdditional:Additional Colors`, Colors: createColorMap(this.AdditionalColors) }
        ];
    }

    GetColorName(color: EventColorEnum): string {
        return this._eventColorClassMapping[color].Name;
    }

    GetColorCssClass(color: EventColorEnum): string {
        return 'color-' + this._eventColorClassMapping[color].Color;
    }

    GetDarkColorCssClass(color: EventColorEnum): string {
        return 'color-dark-' + this._eventColorClassMapping[color].Color;
    }

    GetBackgroundColorCssClass(color: EventColorEnum): string {
        return 'background-' + this._eventColorClassMapping[color].Color;
    }

    GetBackgroundInverseColorCssClass(color: EventColorEnum): string {
        return 'background-inverse-color-' + this._eventColorClassMapping[color].Color;
    }

    GetBorderColorCssClass(color: EventColorEnum, theme: CalendarEventColorTheme = CalendarEventColorTheme.Light): string {
        return 'border-color-' + (theme === CalendarEventColorTheme.HighContrast ? 'high-contrast-' : '') + this._eventColorClassMapping[color].Color;
    }

    GetBorderColorLightCssClass(color: EventColorEnum, theme: CalendarEventColorTheme = CalendarEventColorTheme.Light): string {
        return 'border-color-light-' + (theme === CalendarEventColorTheme.HighContrast ? 'high-contrast-' : '') + this._eventColorClassMapping[color].Color;
    }

    GetCSSPropertyValue(property: string): string {
        return getComputedStyle(document.body).getPropertyValue(property).trim();
    }

    SetCSSPropertyValue(property: string, value: string, targetElement: HTMLElement = document.documentElement): void {
        targetElement.style.setProperty(property, value);
    }

    GetRealBackgroundColor(elem: HTMLElement | null): string {
        const transparent = 'rgba(0, 0, 0, 0)';

        if (!elem) {
            return transparent;
        }

        const backgroundColor = getComputedStyle(elem).backgroundColor;

        if (backgroundColor === transparent) {
            return this.GetRealBackgroundColor(elem.parentElement);
        }

        return backgroundColor;
    }

    GetColorDisplayName(color: EventColorEnum): string {
        return this._eventColorClassMapping[color].Name;
    }

    GetBackgroundColorThemeClass(theme: CalendarEventColorTheme, colorName: string): string {
        return `background-${theme === CalendarEventColorTheme.HighContrast ? 'high-contrast-' : ''}${colorName}`.toLowerCase();
    }

    AddTransparency(color: string, alpha: number): string {
        return '#' + color.replace(/^#/, '').replace(/../g, value => ('0' + Math.min(255, Math.max(0, parseInt(value, 16) + alpha)).toString(16)).substr(-2));
    }


    ApplyBrandColorScale(selectedColor: string, targetElement: HTMLElement = document.documentElement): void {
        const scale = this.GenerateBrandColorScale(selectedColor);

        if (targetElement !== document.documentElement) {
            this.ApplyBrandColorDependentTokens(targetElement);
        }

        Object.entries(scale).forEach(([stop, value]) => {
            this.SetCSSPropertyValue(`--sb_primitive_color_brand_${stop}`, value, targetElement);
        });
    }

    private ApplyBrandColorDependentTokens(targetElement: HTMLElement): void {
        this.GetBrandColorDependentTokens().forEach((value, property) => {
            this.SetCSSPropertyValue(property, value, targetElement);
        });
    }

    private GetBrandColorDependentTokens(): Map<string, string> {
        if (this._brandDependentTokens) {
            return this._brandDependentTokens;
        }

        const declarations = this.GetRootCustomPropertyDeclarations();
        const brandTokens = new Set(BRAND_STOPS.map(stop => `--sb_primitive_color_brand_${stop}`));
        const pendingTokens = [...brandTokens];
        while (pendingTokens.length) {
            const token = pendingTokens.shift()!;

            declarations.forEach((value, property) => {
                if (brandTokens.has(property) || !this.GetReferencedCustomProperties(value).includes(token)) {
                    return;
                }

                brandTokens.add(property);
                pendingTokens.push(property);
            });
        }

        this._brandDependentTokens = new Map(
            Array.from(brandTokens)
                .map(property => [property, declarations.get(property)] as const)
                .filter((entry): entry is [string, string] => !!entry[1])
        );

        return this._brandDependentTokens;
    }

    //Lookup all --sb_* custom properties and values defined in the :root selector
    //Used to determine which custom properties depend on the brand color tokens.
    private GetRootCustomPropertyDeclarations(): Map<string, string> {
        const declarations = new Map<string, string>();

        const visitRules = (rules: CSSRuleList): void => {
            Array.from(rules).forEach(rule => {
                if (rule.type === CSSRule.STYLE_RULE) {
                    const styleRule = rule as CSSStyleRule;
                    const targetsRoot = styleRule.selectorText.split(',').some(selector => selector.trim() === ':root');

                    if (targetsRoot) {
                        Array.from(styleRule.style).forEach(property => {
                            if (property.startsWith('--sb_')) {
                                declarations.set(property, styleRule.style.getPropertyValue(property).trim());
                            }
                        });
                    }
                }

                if ('cssRules' in rule) {
                    visitRules((rule as CSSGroupingRule).cssRules);
                }
            });
        };

        Array.from(document.styleSheets).forEach(styleSheet => {
            try {
                visitRules(styleSheet.cssRules);
            } catch {}
        });

        return declarations;
    }

    //Removes var(--property-name);
    private GetReferencedCustomProperties(value: string): string[] {
        return Array.from(value.matchAll(/var\(\s*(--[\w-]+)/g), match => match[1]);
    }

    private GenerateBrandColorScale(selectedColor: string): BrandScale {
        this.EnsureDefaultBrandScaleFromTokens();

        const normalizedSelectedColor = this.NormalizeCssColorToHex(selectedColor);

        if (this._defaultBrandColorFromTokens && normalizedSelectedColor === this._defaultBrandColorFromTokens) {
            return {
                ...this._defaultBrandScaleFromTokens!
            };
        }

        return this.GenerateWhiteLabelBrandScale(normalizedSelectedColor, this._defaultBrandScaleFromTokens!);
    }


    private EnsureDefaultBrandScaleFromTokens(): void {
        if (this._defaultBrandScaleFromTokens && this._defaultBrandColorFromTokens) {
            return;
        }

        const scale: BrandScale = {};

        BRAND_STOPS.forEach(stop => {
            const tokenName = `--sb_primitive_color_brand_${stop}`;
            const tokenValue = this.GetCSSPropertyValue(tokenName);

            if (!tokenValue) {
                throw new Error(`Missing brand color token: ${tokenName}`);
            }

            scale[stop] = this.NormalizeCssColorToHex(tokenValue);
        });

        this._defaultBrandScaleFromTokens = scale;
        this._defaultBrandColorFromTokens = scale[500];
    }

    private GenerateWhiteLabelBrandScale(selectedColor: string, defaultScale: BrandScale): BrandScale {
        const seed = this.HexToHsl(selectedColor);
        const reference = this.HexToHsl(defaultScale[500]);
        const scale: BrandScale = {};

        BRAND_STOPS.forEach(stop => {
            if (stop === 500) {
                scale[stop] = selectedColor;
                return;
            }

            const stopHsl = this.HexToHsl(defaultScale[stop]);
            const saturation = reference.s === 0 ? seed.s : seed.s * stopHsl.s / reference.s;
            let lightness = seed.l;

            // Preserve each stop shade's relative distance from 500 toward white or black.
            if (stopHsl.l > reference.l) {
                lightness += (100 - seed.l) * (stopHsl.l - reference.l) / (100 - reference.l);
            } else if (stopHsl.l < reference.l) {
                lightness *= stopHsl.l / reference.l;
            }

            scale[stop] = this.HslToHex(seed.h, saturation, lightness);
        });

        return scale;
    }

    private HexToHsl(hex: string): HslColor {
        const normalizedHex = this.NormalizeHex(hex);

        const r = parseInt(normalizedHex.substring(1, 3), 16) / 255;
        const g = parseInt(normalizedHex.substring(3, 5), 16) / 255;
        const b = parseInt(normalizedHex.substring(5, 7), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);

        let h = 0;
        let s = 0;
        const l = (max + min) / 2;

        if (max !== min) {
            const delta = max - min;

            s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

            switch (max) {
                case r:
                    h = (g - b) / delta + (g < b ? 6 : 0);
                    break;

                case g:
                    h = (b - r) / delta + 2;
                    break;

                case b:
                    h = (r - g) / delta + 4;
                    break;
            }

            h /= 6;
        }

        return {
            h: h * 360,
            s: s * 100,
            l: l * 100
        };
    }

    private HslToHex(h: number, s: number, l: number): string {
        h = ((h % 360) + 360) % 360;
        s = this.Clamp(s, 0, 100) / 100;
        l = this.Clamp(l, 0, 100) / 100;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
        const m = l - c / 2;

        let r = 0;
        let g = 0;
        let b = 0;

        if (h < 60) {
            [r, g, b] = [c, x, 0];
        } else if (h < 120) {
            [r, g, b] = [x, c, 0];
        } else if (h < 180) {
            [r, g, b] = [0, c, x];
        } else if (h < 240) {
            [r, g, b] = [0, x, c];
        } else if (h < 300) {
            [r, g, b] = [x, 0, c];
        } else {
            [r, g, b] = [c, 0, x];
        }

        const toHex = (value: number) =>
            Math.round((value + m) * 255)
                .toString(16)
                .padStart(2, '0');

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    }

    private NormalizeCssColorToHex(color: string): string {
        const value = color.trim();

        if (value.startsWith('#') || /^[0-9a-fA-F]{3}$/.test(value) || /^[0-9a-fA-F]{6}$/.test(value)) {
            return this.NormalizeHex(value);
        }

        const rgbaMatch = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)$/i);

        if (rgbaMatch) {
            const r = Number(rgbaMatch[1]);
            const g = Number(rgbaMatch[2]);
            const b = Number(rgbaMatch[3]);

            return this.RgbToHex(r, g, b);
        }

        throw new Error(`Unsupported color format: ${color}`);
    }

    private NormalizeHex(hex: string): string {
        let value = hex.trim();

        if (!value.startsWith('#')) {
            value = `#${value}`;
        }

        if (/^#[0-9a-fA-F]{3}$/.test(value)) {
            const r = value[1];
            const g = value[2];
            const b = value[3];

            return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
        }

        if (!/^#[0-9a-fA-F]{6}$/.test(value)) {
            throw new Error(`Invalid hex color: ${hex}`);
        }

        return value.toUpperCase();
    }

    private RgbToHex(r: number, g: number, b: number): string {
        const toHex = (value: number) => this.Clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    }

    private Clamp(value: number, min: number, max: number): number {
        return Math.min(max, Math.max(min, value));
    }
}
